/**
 * Полоса возврата на экране детали — остров под шапкой.
 *
 * На узком экране повторяет шапку оболочки: та же пилюля на `--glass-*`, те же
 * круглые кнопки, стоит по её кромкам. Липнет к верху — экран детали на
 * телефоне длинный, и уехавшая с первым движением пальца кнопка «назад»
 * означала бы выход только системным жестом.
 *
 * Справа — две разные вещи, и порог у них разный.
 *
 * `extra` — действия НАД содержимым страницы (у заявки это её кнопки и кебаб).
 * Видны на любой ширине: полоса липкая, и только здесь действие не уезжает за
 * кромку при первом обороте колеса.
 *
 * `actions` и кнопка чата — навигация ПО странице, и нужна она лишь там, где
 * страница сложилась в одну колонку (≤960): пока колонки стоят рядом, до чата
 * и управления прокручивать нечего. Кнопку чата полоса ведёт сама: пока чат
 * ниже экрана — «к чату», как только доехали — «наверх». Цели задаются якорями
 * `data-anchor="…"` в содержимом, а не ref'ами: полоса и чат лежат в разных
 * компонентах, и протаскивать ref через три слоя было бы дороже одного
 * атрибута.
 *
 * `title` — то, что полоса показывает посередине, когда предмет страницы уехал
 * под неё (`titleVisible`): профиль кладёт сюда мини-аватар и имя, как
 * сворачивающаяся шапка профиля Telegram. Без `title` полоса прежняя.
 *
 * Прокрутку полоса ищет сама — ближайший скроллпорт выше по дереву: в
 * `/tickets/view/:id` это лента оболочки, в портале — вьюпорт drawer'а.
 *
 * @use экран детали заявки: шапка возврата с действиями по странице.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUp } from 'lucide-react';
import { ChatIcon } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui';
import styles from './DetailBar.module.css';

export interface DetailBarAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  anchor?: string;
  onClick?: () => void;
}

export interface DetailBarProps {
  backTo: string;
  onBack?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  backLabel?: string;
  actions?: DetailBarAction[];
  extra?: React.ReactNode;
  title?: React.ReactNode;
  titleVisible?: boolean;
  iconOnly?: boolean;
  noChatButton?: boolean;
  className?: string;
}

function scrollParentOf(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return (document.scrollingElement ?? document.documentElement) as HTMLElement;
}

const CHAT_AHEAD_PX = 120;
const SCROLLED_PX = 8;

export const DetailBar: React.FC<DetailBarProps> = ({
  backTo,
  onBack,
  backLabel = 'К списку заявок',
  actions = [],
  extra,
  title,
  titleVisible = false,
  iconOnly = false,
  className,
  noChatButton = false,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [chatAhead, setChatAhead] = useState<boolean | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const scrollTo = useCallback((anchor: string) => {
    const bar = barRef.current;
    if (!bar) return;
    const parent = scrollParentOf(bar);
    const target = parent.querySelector<HTMLElement>(`[data-anchor="${anchor}"]`);
    if (!target) return;
    const parentTop = parent.getBoundingClientRect().top;
    const offset = bar.getBoundingClientRect().bottom - parentTop + 8;
    parent.scrollTo({
      top: parent.scrollTop + target.getBoundingClientRect().top - parentTop - offset,
      behavior: 'smooth',
    });
  }, []);

  const scrollTop = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    scrollParentOf(bar).scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const parent = scrollParentOf(bar);
      const chat = parent.querySelector<HTMLElement>('[data-anchor="chat"]');
      const top = parent.scrollTop;
      setScrolled((was) => (was ? top > 0 : top > SCROLLED_PX));
      const bottom =
        parent === document.scrollingElement || parent === document.documentElement
          ? window.innerHeight
          : parent.getBoundingClientRect().bottom;
      setChatAhead(chat ? chat.getBoundingClientRect().top > bottom - CHAT_AHEAD_PX : null);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    schedule();
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const chatButton = noChatButton ? null : chatAhead ? (
    <IconButton label="К чату" className={styles.iconBtn} onClick={() => scrollTo('chat')}>
      <ChatIcon size={16} aria-hidden />
    </IconButton>
  ) : scrolled ? (
    <IconButton label="Наверх" className={styles.iconBtn} onClick={scrollTop}>
      <ArrowUp size={16} aria-hidden />
    </IconButton>
  ) : null;

  return (
    <div ref={barRef} className={cn(styles.bar, className)} data-scrolled={scrolled || undefined}>
      <Link
        className={styles.back}
        to={backTo}
        onClick={onBack}
        aria-label={iconOnly ? backLabel : undefined}
        title={iconOnly ? backLabel : undefined}
      >
        <span className={styles.backIcon}>
          <ArrowLeft size={16} aria-hidden />
        </span>
        {!iconOnly && <span className={styles.backLabel}>{backLabel}</span>}
      </Link>
      {title && (
        <div className={cn(styles.title, titleVisible && styles.titleVisible)} aria-hidden={!titleVisible}>
          {title}
        </div>
      )}
      <div className={styles.right}>
        {extra && <div className={styles.pageActions}>{extra}</div>}
        <div className={styles.actions}>
          {actions.map((a) => (
            <IconButton
              key={a.key}
              label={a.label}
              className={styles.iconBtn}
              onClick={() => {
                a.onClick?.();
                if (a.anchor) scrollTo(a.anchor);
              }}
            >
              {a.icon}
            </IconButton>
          ))}
          {chatButton}
        </div>
      </div>
    </div>
  );
};
